import { Component, AfterViewInit, Input, ElementRef, ViewChild, OnDestroy, HostListener, SimpleChanges, OnChanges, NgZone } from '@angular/core';

@Component({
  selector: 'nc-auto-scroll-text',
  templateUrl: './auto-scroll-text.component.html',
  styleUrls: ['./auto-scroll-text.component.less']
})
export class AutoScrollTextComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('textContainer') textContainer!: ElementRef;
  @ViewChild('scrollText') scrollText!: ElementRef;

  private animationId!: number;
  private position: number = 0;
  private isPaused: boolean = false;
  private lastTimestamp: number = 0;
  private pauseStartTime: number = 0;

  /** 滚动文本 */
  @Input() ncText: string = '';
  /** 滚动速度，单位为像素/秒 */
  @Input() ncSpeed: number = 50;
  /** 是否开启无缝滚动 */
  private _loop: boolean = false;
  @Input()
  set ncLoop(val: boolean | string) {
    this._loop = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncLoop(): boolean {
    return this._loop;
  }
  /** 开启无缝滚动时，间距，单位为像素 */
  @Input() ncGap: number = 0;
  /** 是否启用鼠标悬停暂停滚动 */
  private _pause: boolean = false;
  @Input()
  set ncPause(val: boolean | string) {
    this._pause = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncPause(): boolean {
    return this._pause;
  }

  constructor(private ngZone: NgZone) { }

  ngAfterViewInit(): void {
    this.resetScroll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ncText'] && !changes['ncText'].firstChange) {
      this.resetScroll();
    }
  }

  ngOnDestroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  // 鼠标移入事件处理
  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.ncPause) {
      this.isPaused = true;
      this.pauseStartTime = performance.now();
    }
  }

  // 鼠标移出事件处理
  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.ncPause && this.isPaused) {
      this.isPaused = false;
      const pauseDuration = performance.now() - this.pauseStartTime;
      this.lastTimestamp += pauseDuration;
    }
  }

  private startScrolling(): void {
    // 检查文本是否需要滚动
    if (!this.textContainer || !this.scrollText) return;

    this.ngZone.runOutsideAngular(() => {
      const containerWidth = this.textContainer.nativeElement.offsetWidth;
      const textWidth = this.scrollText.nativeElement.offsetWidth;

      // 只有当文本宽度大于容器宽度时才滚动
      if (textWidth > containerWidth) {
        this.lastTimestamp = 0;

        const animate = (timestamp: number) => {
          if (!this.lastTimestamp) this.lastTimestamp = timestamp;

          // 当不处于暂停状态时才计算位移
          if (!this.isPaused) {
            const elapsed = timestamp - this.lastTimestamp;

            // 根据时间和速度计算位移
            this.position += (elapsed / 1000) * this.ncSpeed;

            // 当文本的最后一个字符出现在视窗内时创建复制文本实现无缝衔接
            if (this.position >= textWidth - containerWidth) {
              if (this.ncLoop) {
                // 让文本平滑地重新开始滚动，而不是突然跳回开始位置
                // 将第二份文本复制品拼接在原文本后面，实现无缝衔接效果
                if (!this.scrollText.nativeElement.querySelector('.duplicate-text')) {
                  const duplicateText = document.createElement('span');
                  duplicateText.classList.add('duplicate-text');
                  duplicateText.textContent = this.ncText;
                  duplicateText.style.marginLeft = `${this.ncGap}px`;
                  this.scrollText.nativeElement.appendChild(duplicateText);
                }
              } else {
                // 不开启无缝滚动时，当文本的最后一个字符出现在视窗内时重新开始
                this.position = 0;
              }
            }

            // 实现真正的无缝滚动：当滚动位置超过第一段文本宽度时，重置位置并保持视觉连续
            if (this.ncLoop && this.position >= textWidth + this.ncGap) {
              // 重置位置，但保持视觉效果不变
              this.position = 0;

              // 移除复制的文本元素
              const duplicateText = this.scrollText.nativeElement.querySelector('.duplicate-text');
              if (duplicateText) {
                this.scrollText.nativeElement.removeChild(duplicateText);
              }

              // 立即重新设置transform，避免闪烁
              this.scrollText.nativeElement.style.transform = 'translateX(0)';

              // 立即创建新的复制文本
              const newDuplicateText = document.createElement('span');
              newDuplicateText.classList.add('duplicate-text');
              newDuplicateText.textContent = this.ncText;
              newDuplicateText.style.marginLeft = `${this.ncGap}px`;
              this.scrollText.nativeElement.appendChild(newDuplicateText);
            } else {
              // 应用位移
              this.scrollText.nativeElement.style.transform = `translateX(-${this.position}px)`;
            }

            // 更新时间戳
            this.lastTimestamp = timestamp;
          }

          this.animationId = requestAnimationFrame(animate);
        };

        this.animationId = requestAnimationFrame(animate);
      }
    });
  }

  private resetScroll(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
    this.position = 0;
    this.isPaused = false;
    if (this.scrollText) {
      this.scrollText.nativeElement.style.transform = 'translateX(0)';
      // 清除可能存在的重复文本
      const duplicateText = this.scrollText.nativeElement.querySelector('.duplicate-text');
      if (duplicateText) {
        this.scrollText.nativeElement.removeChild(duplicateText);
      }
    }
    this.startScrolling();
  }
}
